import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ArrowRight, Award, Heart } from 'lucide-react';
import CinematicVideoSection from '../components/CinematicVideoSection';
import ReviewsWidget from '../components/ReviewsWidget';
import VideoTestimonialsSection from '../components/VideoTestimonialsSection';
import PageSEO from '../components/PageSEO';
import { trackWhatsAppClick } from '../utils/trackWhatsApp';

const HomePage = () => {
  const { t, language } = useLanguage();

  return (
    <div data-testid="home-page">
      <PageSEO path="/" />
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              {/* Badge */}
              <div className="inline-flex items-center space-x-2 bg-blue-50 px-4 py-2 rounded-full mb-6 border border-blue-100">
                <Award className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-gray-700">{t.hero.badge}</span>
              </div>

              {/* Main Title */}
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 leading-tight mb-6" style={{fontFamily: "'Cormorant Garamond', serif"}}>
                {t.hero.title}
                <span className="block text-blue-600">{t.hero.titleHighlight}</span>
              </h1>

              <p className="text-xl text-gray-600 leading-relaxed max-w-xl mb-4">
                {t.hero.description}
              </p>

              <p className="text-base text-gray-500 leading-relaxed max-w-xl mb-4">
                {language === 'it' 
                  ? 'Scopri le tue radici italiane con un servizio di genealogia professionale con sede in Italia. Offriamo recupero documenti, ricerca genealogica e viaggi personalizzati alla scoperta del tuo patrimonio.' 
                  : language === 'es' 
                  ? 'Descubre tus raíces italianas con un servicio de genealogía profesional con sede en Italia. Ofrecemos recuperación de documentos, investigación genealógica y experiencias de viaje personalizadas.' 
                  : language === 'pt' 
                  ? 'Descubra suas raízes italianas com um serviço de genealogia profissional sediado na Itália. Oferecemos recuperação de documentos, pesquisa genealógica e experiências de viagem personalizadas.' 
                  : 'Discover your Italian roots with a professional genealogy service based in Italy. We provide document retrieval, ancestry research, and personalized heritage travel experiences.'}
              </p>

              {/* New Intro Text */}
              <p className="text-lg italic text-gray-700 leading-relaxed max-w-xl mb-2" style={{fontFamily: "'Cormorant Garamond', serif"}}>
                {t.hero.introHeadline}
              </p>
              <p className="text-sm text-gray-500 leading-relaxed max-w-xl mb-8">
                {t.hero.introSupport}
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/free-evaluation"
                  className="inline-flex items-center justify-center px-6 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors shadow-lg"
                  data-testid="hero-free-evaluation"
                >
                  {language === 'it' ? '🎁 Valutazione Gratuita' : language === 'es' ? '🎁 Evaluación Gratuita' : language === 'pt' ? '🎁 Avaliação Gratuita' : '🎁 Free Evaluation'}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
                  data-testid="hero-cta"
                >
                  {t.hero.cta}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </div>

              {/* Stats */}
              <div className="mt-12 grid grid-cols-3 gap-6">
                <div>
                  <div className="text-3xl font-bold text-gray-900" style={{fontFamily: "'Cormorant Garamond', serif"}}>500+</div>
                  <div className="text-sm text-gray-500 mt-1">{t.hero.stats.projects}</div>
                </div>
                <div className="border-l border-gray-200 pl-6">
                  <div className="text-3xl font-bold text-gray-900" style={{fontFamily: "'Cormorant Garamond', serif"}}>30+</div>
                  <div className="text-sm text-gray-500 mt-1">{t.hero.stats.experience}</div>
                </div>
                <div className="border-l border-gray-200 pl-6">
                  <div className="text-3xl font-bold text-gray-900" style={{fontFamily: "'Cormorant Garamond', serif"}}>40+</div>
                  <div className="text-sm text-gray-500 mt-1">{t.hero.stats.countries}</div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_return-dialog/artifacts/p78lohqc_9c0240eb-92f6-4764-8bc6-14982a22b46d.jpg"
                  alt="Foto storica di famiglia italiana"
                  className="w-full h-auto object-contain"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white p-5 rounded-xl shadow-xl border border-gray-100">
                <div className="flex items-center space-x-4">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_return-dialog/artifacts/of4eva1w_IMG-20260114-WA0048.jpg"
                    alt="Laura Stella"
                    className="w-14 h-14 rounded-full object-cover ring-2 ring-blue-100"
                  />
                  <div>
                    <div className="font-bold text-gray-900 text-lg" style={{fontFamily: "'Cormorant Garamond', serif"}}>Laura Stella & Giosia Caliò</div>
                    <div className="text-sm text-gray-600 font-medium">
                      {language === 'it' ? 'Fondatori — Italian Roots Discovery' : 
                       language === 'es' ? 'Fundadores — Italian Roots Discovery' :
                       language === 'pt' ? 'Fundadores — Italian Roots Discovery' :
                       'Founders — Italian Roots Discovery'}
                    </div>
                    <div className="text-xs text-blue-600 font-semibold mt-0.5">
                      {language === 'it' ? '30+ anni di esperienza' : 
                       language === 'es' ? '30+ años de experiencia' :
                       language === 'pt' ? '30+ anos de experiência' :
                       '30+ years of experience'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cinematic Video Intro + Journey */}
      <section className="py-16 md:py-20 bg-gradient-to-b from-white to-gray-50" data-testid="cinematic-intro-section">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-xs sm:text-sm tracking-[0.3em] uppercase text-blue-600 font-semibold mb-4">
            {language === 'it' ? 'Da Nord a Sud' : language === 'es' ? 'De Norte a Sur' : language === 'pt' ? 'De Norte a Sul' : 'From North to South'}
          </p>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-5"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            {language === 'it'
              ? 'La Tua Storia Vive in Ogni Angolo d\'Italia'
              : language === 'es'
              ? 'Tu Historia Vive en Cada Rincon de Italia'
              : language === 'pt'
              ? 'Sua Historia Vive em Cada Canto da Italia'
              : 'Your Story Lives in Every Corner of Italy'}
          </h2>
          <p className="text-base sm:text-lg text-gray-500 leading-relaxed max-w-2xl mx-auto" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {language === 'it'
              ? 'Scopri i luoghi, le tradizioni e le emozioni che ancora oggi custodiscono le tue radici.'
              : language === 'es'
              ? 'Descubre los lugares, las tradiciones y las emociones que aun hoy guardan tus raices.'
              : language === 'pt'
              ? 'Descubra os lugares, as tradicoes e as emocoes que ainda hoje guardam as suas raizes.'
              : 'Discover the places, the traditions, and the emotions that still carry your roots today.'}
          </p>
        </div>
      </section>
      <CinematicVideoSection />

      {/* Image Gallery - Archives */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <img 
              src="https://images.unsplash.com/photo-1524995997946-a1c2e315a42f?w=400" 
              alt="Historical books"
              className="rounded-lg h-48 w-full object-cover shadow-md"
            />
            <img 
              src="https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=400" 
              alt="Library archives"
              className="rounded-lg h-48 w-full object-cover shadow-md"
            />
            <img 
              src="https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?w=400" 
              alt="Old documents"
              className="rounded-lg h-48 w-full object-cover shadow-md"
            />
            <img 
              src="https://images.unsplash.com/photo-1551029506-0807df4e2031?w=400" 
              alt="Genealogy research"
              className="rounded-lg h-48 w-full object-cover shadow-md"
            />
          </div>
        </div>
      </section>

      {/* Success Stories Preview */}
      <section className="py-20 bg-white/70 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900" style={{fontFamily: "'Cormorant Garamond', serif"}}>{t.stories.title}</h2>
            <p className="mt-4 text-gray-600">{t.stories.subtitle}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {t.stories.items.map((story, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-xl shadow-sm"
                data-testid={`story-card-${index}`}
              >
                <div className="flex items-center space-x-1 text-yellow-400 mb-4">
                  {"★★★★★".split('').map((star, i) => (
                    <span key={i}>{star}</span>
                  ))}
                </div>
                <p className="text-gray-600 italic mb-4">&ldquo;{story.quote}&rdquo;</p>
                <div className="font-semibold text-gray-900">{story.name}</div>
                <div className="text-sm text-gray-500">{story.location}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Video Testimonials */}
      <VideoTestimonialsSection />

      {/* Results & Map Preview */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Gallery Preview */}
            <Link to="/gallery" className="group bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-lg transition-all" data-testid="home-gallery-link">
              <div className="h-48 bg-gradient-to-r from-amber-100 to-orange-100 flex items-center justify-center relative overflow-hidden">
                <img src="https://images.unsplash.com/photo-1544027993-37dbfe43562a?w=600" alt="Gallery" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <div className="absolute bottom-4 left-4 text-white">
                  <span className="text-2xl font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>500+</span>
                  <span className="text-sm ml-2 text-white/80">{language === 'it' ? 'famiglie aiutate' : 'families helped'}</span>
                </div>
              </div>
              <div className="p-5">
                <h3 className="text-xl font-bold text-gray-900 mb-1 group-hover:text-blue-600 transition" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  {language === 'it' ? 'I Nostri Risultati' : language === 'es' ? 'Nuestros Resultados' : language === 'pt' ? 'Nossos Resultados' : 'Our Results'}
                </h3>
                <p className="text-sm text-gray-500">{language === 'it' ? 'Scopri casi reali di ricerche genealogiche completate con successo' : 'Discover real cases of successful genealogical research'}</p>
                <span className="inline-flex items-center text-sm text-blue-600 font-medium mt-3 group-hover:gap-2 transition-all">
                  {language === 'it' ? 'Vedi i casi' : 'See cases'} <ArrowRight className="w-4 h-4 ml-1" />
                </span>
              </div>
            </Link>

            {/* Map Preview */}
            <Link to="/italy-map" className="group bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-lg transition-all" data-testid="home-map-link">
              <div className="h-48 bg-gradient-to-r from-emerald-100 to-teal-100 flex items-center justify-center relative overflow-hidden">
                <img src="https://images.unsplash.com/photo-1515859005217-8a1f08870f59?w=600" alt="Italy Map" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <div className="absolute bottom-4 left-4 text-white">
                  <span className="text-2xl font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>15+</span>
                  <span className="text-sm ml-2 text-white/80">{language === 'it' ? 'regioni coperte' : 'regions covered'}</span>
                </div>
              </div>
              <div className="p-5">
                <h3 className="text-xl font-bold text-gray-900 mb-1 group-hover:text-emerald-600 transition" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  {language === 'it' ? 'Mappa delle Origini' : language === 'es' ? 'Mapa de Orígenes' : language === 'pt' ? 'Mapa das Origens' : 'Origins Map'}
                </h3>
                <p className="text-sm text-gray-500">{language === 'it' ? 'Esplora dove abbiamo aiutato famiglie in tutta Italia' : 'Explore where we helped families across Italy'}</p>
                <span className="inline-flex items-center text-sm text-emerald-600 font-medium mt-3 group-hover:gap-2 transition-all">
                  {language === 'it' ? 'Esplora la mappa' : 'Explore the map'} <ArrowRight className="w-4 h-4 ml-1" />
                </span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Donation Banner */}
      <section className="py-12 bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 border-y border-amber-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/donate"
            className="group relative overflow-hidden flex flex-col md:flex-row items-center gap-6 bg-white rounded-2xl shadow-md hover:shadow-xl border border-amber-100 p-6 md:p-8 transition-all hover:-translate-y-0.5"
            data-testid="home-donate-banner"
          >
            <div className="absolute -right-12 -top-12 w-44 h-44 rounded-full bg-gradient-to-br from-amber-200/30 to-orange-300/20 blur-3xl group-hover:scale-125 transition-transform duration-700" />
            <div className="relative w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 text-white flex items-center justify-center shadow-lg shadow-amber-500/30 flex-shrink-0 group-hover:scale-110 transition-transform">
              <Heart className="w-8 h-8" fill="currentColor" />
            </div>
            <div className="relative flex-1 text-center md:text-left">
              <h3 className="text-2xl font-bold text-gray-900 mb-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                {language === 'it' ? 'Donazione Volontaria' : language === 'es' ? 'Donación Voluntaria' : language === 'pt' ? 'Doação Voluntária' : 'Voluntary Donation'}
              </h3>
              <p className="text-gray-600 text-sm md:text-base">
                {language === 'it' ? 'Sostieni la ricerca genealogica e la preservazione del patrimonio documentale italiano. Ogni contributo conta.' : language === 'es' ? 'Apoya la investigación genealógica y la preservación del patrimonio documental italiano. Cada contribución cuenta.' : language === 'pt' ? 'Apoie a pesquisa genealógica e a preservação do patrimônio documental italiano. Cada contribuição conta.' : 'Support genealogical research and the preservation of Italian documentary heritage. Every contribution counts.'}
              </p>
            </div>
            <div className="relative inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 text-white font-semibold shadow-md group-hover:shadow-lg group-hover:gap-3 transition-all">
              {language === 'it' ? 'Dona Ora' : language === 'es' ? 'Donar Ahora' : language === 'pt' ? 'Doar Agora' : 'Donate Now'}
              <ArrowRight className="w-4 h-4" />
            </div>
          </Link>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-6" style={{fontFamily: "'Cormorant Garamond', serif"}}>
            {t.contact.subtitle}
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/booking"
              className="inline-flex items-center px-8 py-4 bg-white text-blue-600 font-medium rounded-lg hover:bg-gray-100 transition-colors shadow-lg"
              data-testid="cta-booking"
            >
              {language === 'it' ? 'Prenota una Consulenza' : language === 'es' ? 'Reserva una Consulta' : language === 'pt' ? 'Agende uma Consulta' : 'Book a Consultation'}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <Link
              to="/services"
              className="inline-flex items-center px-8 py-4 bg-blue-500 text-white font-medium rounded-lg hover:bg-blue-400 transition-colors"
              data-testid="cta-contact"
            >
              {t.hero.cta}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <a
              onClick={() => trackWhatsAppClick('hero_cta', '/home', 'Laura', language)}
              href={`https://wa.me/393318792031?text=${encodeURIComponent('Ciao! Sono interessato ai servizi di Italian Roots Discovery. Vorrei saperne di più sulla ricerca genealogica e la cittadinanza italiana.')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-8 py-4 bg-green-500 text-white font-medium rounded-lg hover:bg-green-600 transition-colors"
            >
              WhatsApp
              <ArrowRight className="ml-2 w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* Reviews */}
      <ReviewsWidget />
    </div>
  );
};

export default HomePage;
